import type {MouseEvent, RefObject} from 'react';
import {useEffect, useRef, useState} from 'react';

interface BrandKitRenameParams {
  /** The kit's name as saved. */
  savedName: string;
  /** Committing a new name; without it the kit cannot be renamed here. */
  onRename?: (newBrandName: string) => void;
  /** True while a committed name is being saved, so the new name shows until the store has it. */
  isRenameSaving: boolean;
  /** Bumped by a control elsewhere (mobile's pencil button) to open the field; the starting value does nothing. */
  renameRequestCount: number;
  /** Told each time the field opens or closes, so a rename button elsewhere can stand down meanwhile. */
  onRenamingChange?: (isRenaming: boolean) => void;
  /**
   * Called once the field has closed after Enter or Escape, whose focus went down with the field. A rename
   * ended by the pointer (a click away, the check) leaves focus where the user put it.
   */
  onRenameEndedFromKeyboard?: () => void;
  /**
   * The pill or list row holding the field. It is kept in view while renaming: the phone row scrolls sideways
   * and a long "More brand kits" list scrolls down, so the row -- and the check at its end -- may sit out of view.
   */
  rowRef?: RefObject<HTMLElement | null>;
}

export interface BrandKitRenameState {
  canRename: boolean;
  isRenaming: boolean;
  /** What the kit's name reads as right now: the saved name, or the one being saved. */
  displayName: string;
  draftName: string;
  inputRef: RefObject<HTMLInputElement | null>;
  setDraftName: (draftName: string) => void;
  startRenaming: (event: MouseEvent) => void;
  commitRename: () => void;
  cancelRename: () => void;
  commitRenameFromKeyboard: () => void;
  cancelRenameFromKeyboard: () => void;
}

/**
 * Renaming a kit in place, shared by the row's pills and the "+N more" list: the field opens with the caret after the name,
 * an all-whitespace or unchanged name saves nothing, and cancelling puts the saved name back.
 */
export const useBrandKitRename = ({savedName, onRename, isRenameSaving, renameRequestCount, onRenamingChange, onRenameEndedFromKeyboard, rowRef}: BrandKitRenameParams): BrandKitRenameState => {
  const [isRenaming, setIsRenaming] = useState(false);
  const [draftName, setDraftName] = useState(savedName);
  const inputRef = useRef<HTMLInputElement>(null);
  const isEndingFromKeyboardRef = useRef(false);
  const canRename = onRename !== undefined;

  useEffect(() => {
    onRenamingChange?.(isRenaming);

    if (!isRenaming) {
      notifyIfEndedFromKeyboard();
      return;
    }

    const input = inputRef.current;

    if (input) {
      input.focus({preventScroll: true});
      input.setSelectionRange(input.value.length, input.value.length);
      // A focus that does not scroll leaves a long name showing its start, with the caret out of sight at its end.
      input.scrollLeft = input.scrollWidth;
    }
  }, [isRenaming]);

  /** The field is focused without scrolling, so the row is brought into view here, and again as typing grows it. */
  useEffect(() => {
    if (isRenaming) {
      rowRef?.current?.scrollIntoView({block: 'nearest', inline: 'nearest'});
    }
  }, [isRenaming, draftName]);

  useEffect(() => {
    if (renameRequestCount === 0 || !canRename) {
      return;
    }

    setDraftName(savedName);
    setIsRenaming(true);
  }, [renameRequestCount]);

  const startRenaming = (event: MouseEvent): void => {
    event.stopPropagation();
    setDraftName(savedName);
    setIsRenaming(true);
  };

  const commitRename = (): void => {
    setIsRenaming(false);
    const trimmedName = draftName.trim();

    if (trimmedName && trimmedName !== savedName) {
      onRename?.(trimmedName);
    }
  };

  const cancelRename = (): void => {
    setIsRenaming(false);
    setDraftName(savedName);
  };

  const commitRenameFromKeyboard = (): void => {
    isEndingFromKeyboardRef.current = true;
    commitRename();
  };

  const cancelRenameFromKeyboard = (): void => {
    isEndingFromKeyboardRef.current = true;
    cancelRename();
  };

  const notifyIfEndedFromKeyboard = (): void => {
    if (!isEndingFromKeyboardRef.current) {
      return;
    }

    isEndingFromKeyboardRef.current = false;
    onRenameEndedFromKeyboard?.();
  };

  return {
    canRename,
    isRenaming,
    displayName: isRenameSaving ? draftName.trim() : savedName,
    draftName,
    inputRef,
    setDraftName,
    startRenaming,
    commitRename,
    cancelRename,
    commitRenameFromKeyboard,
    cancelRenameFromKeyboard,
  };
};
